import {
  formatQuery,
  ValueProcessorByRule,
  defaultValueProcessorByRule,
  RuleType,
} from 'react-querybuilder';
import type { RuleGroupType } from 'react-querybuilder';

import { DataLake } from '@finley-technologies/finley-types';

import { AthenaTablePropertyGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

export const addLeadingZeroForDecimal = (exclusionSqlClause: string) => {
  const regexToExcludeQuotes = /'[^']*'|([^']+)/g;
  return exclusionSqlClause.replace(regexToExcludeQuotes, (m, group1) => {
    if (!group1) return m;
    return group1.replace(/([(|\s])(\.\d+)([)|\s|,])/gi, `$10$2$3`);
  });
};

export const addTrailingAndLeadingZeroForDecimal = (
  exclusionSqlClause: string,
) => {
  const regexDecimalWithoutLeadingZero = /^\s*(\.\d+)\s*/;

  const regexDecimalWithoutEndingZero = /^\s*(\d+)\s*$/;

  if (regexDecimalWithoutLeadingZero.test(exclusionSqlClause)) {
    const output = exclusionSqlClause.replace(
      regexDecimalWithoutLeadingZero,
      (m, group) => {
        if (!group) return m.trim();
        return group.replace(regexDecimalWithoutLeadingZero, `0$1`);
      },
    );
    return output.trim();
  }

  if (regexDecimalWithoutEndingZero.test(exclusionSqlClause)) {
    const output = exclusionSqlClause.replace(
      regexDecimalWithoutEndingZero,
      (m, group) => {
        if (!group) return m.trim();
        return group.replace(regexDecimalWithoutEndingZero, `$1.0`);
      },
    );
    return output.trim();
  }
  return exclusionSqlClause.trim();
};

export const convertStringsToBoolean = (exclusionSqlClause: string) => {
  return exclusionSqlClause
    .replace(/'TRUE'/gi, 'TRUE')
    .replace(/'FALSE'/gi, 'FALSE');
};

export const convertBooleansToString = (exclusionSqlClause: string) => {
  return exclusionSqlClause
    .replace(/TRUE/gi, "'TRUE'")
    .replace(/FALSE/gi, "'FALSE'");
};

const getValueProcessorByAthenaColumnType = (
  colType: DataLake.AthenaTablePropertyTypeUnion,
): ValueProcessorByRule => {
  switch (colType) {
    case 'boolean':
      return (rule) => `${rule.value.toUpperCase()}`; // make sure booleans in all caps
    case 'bigint':
    case 'tinyint':
    case 'smallint':
    case 'int':
    case 'integer': // TODO: add special handling for double + float to add leading zero
      return (rule) => rule.value;
    case 'double':
    case 'float':
    case 'decimal':
      return (rule) => addTrailingAndLeadingZeroForDecimal(rule.value);
    case 'date':
      return (rule) => `DATE('${rule.value}')`;
    case 'string':
    case 'char':
    case 'varchar':
    case 'binary': // currently unused
    case 'array': // currently unused
    case 'map': // currently unused
    case 'struct': // currently unused
    case 'timestamp': // TODO support timestamp
    default:
      return defaultValueProcessorByRule;
  }
};

export const athenaValueProcessorByRule = (
  athenaCols: AthenaTablePropertyGQLType[],
): ValueProcessorByRule => {
  return (rule: RuleType) => {
    const athenaCol = athenaCols.find((f) => f.name === rule.field);

    if (!athenaCol || rule.operator === 'null' || rule.operator === 'notNull') {
      return defaultValueProcessorByRule(rule);
    }
    return getValueProcessorByAthenaColumnType(athenaCol.type)(rule);
  };
};

export const formatAthenaSqlString = (
  query: RuleGroupType,
  athenaCols: AthenaTablePropertyGQLType[],
) => {
  return query.rules.length === 0
    ? ''
    : formatQuery(query, {
        format: 'sql',
        quoteFieldNamesWith: '"',
        valueProcessor: athenaValueProcessorByRule(athenaCols),
      });
};
